import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  FileText, Plus, ChevronLeft, Copy, Download, Upload, Archive, Pencil, Power, FileDown, ListChecks, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { qmsPlanService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATO_BADGE = {
  Bozza: 'bg-amber-100 text-amber-800 border-amber-300',
  Attivo: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Obsoleto: 'bg-slate-200 text-slate-600 border-slate-300',
  Attiva: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Obsoleta: 'bg-slate-200 text-slate-600 border-slate-300',
};

const EMPTY_ROW = {
  fase_processo: 'OP10',
  numero_caratteristica: '',
  riferimento_pallinatura: '',
  descrizione_caratteristica: '',
  tipo_caratteristica: 'Dimensionale',
  tipo_valutazione: 'Numerica con nominale e tolleranze',
  specifica_nominale: '',
  tolleranza_min: '',
  tolleranza_max: '',
  limite_min_assoluto: '',
  limite_max_assoluto: '',
  unita_misura: '',
  numero_decimali: '',
  metodo_controllo: 'Micrometro',
  strumento_consigliato: '',
  valore_conforme_okko: '',
  valore_conforme_presenza_assenza: '',
  obbligatorio: true,
  richiede_allegato: false,
  tipo_allegato_richiesto: '',
  note_operative: '',
  note_valutazione: '',
};

const EMPTY_APP = (causale, ordine) => ({
  causale_controllo: causale,
  attiva: false,
  obbligatoria: false,
  frequenza_controllo: '',
  quantita_campione: '',
  richiede_allegato: false,
  tipo_allegato_richiesto: '',
  note_specifiche: '',
  ordine_visualizzazione: ordine,
});

export default function QmsPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'quality');
  const fileInputRef = useRef(null);

  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [enums, setEnums] = useState(null);
  const [showObsolete, setShowObsolete] = useState(false);

  const [rowDialog, setRowDialog] = useState({ open: false, mode: 'create', data: { ...EMPTY_ROW } });
  const [dupDialog, setDupDialog] = useState({ open: false, new_revision: '', new_id_configurazione: '', new_titolo: '' });
  const [appDialog, setAppDialog] = useState({ open: false, rowId: null, rowLabel: '', apps: [] });

  useEffect(() => {
    qmsPlanService.getEnums().then(r => setEnums(r.data));
    load();
  }, [id]);
  useEffect(() => { loadRows(); }, [showObsolete, id]);

  const load = async () => {
    try {
      const r = await qmsPlanService.getById(id);
      setPlan(r.data);
    } catch {
      toast.error('Piano non trovato');
      navigate('/qms-plans');
    }
  };
  const loadRows = async () => {
    try {
      const r = await qmsPlanService.listRows(id, showObsolete);
      const sorted = [...(r.data || [])].sort((a, b) => {
        const an = parseFloat(String(a.numero_caratteristica || '').replace(',', '.'));
        const bn = parseFloat(String(b.numero_caratteristica || '').replace(',', '.'));
        const aNum = !isNaN(an);
        const bNum = !isNaN(bn);
        if (aNum && bNum) return an - bn;
        if (aNum) return -1;
        if (bNum) return 1;
        return String(a.numero_caratteristica || '').localeCompare(String(b.numero_caratteristica || ''));
      });
      setRows(sorted);
    } catch { /* swallow */ }
  };

  const openCreateRow = () => setRowDialog({ open: true, mode: 'create', data: { ...EMPTY_ROW } });
  const openEditRow = (r) => setRowDialog({
    open: true,
    mode: 'edit',
    data: {
      ...EMPTY_ROW,
      ...r,
      riferimento_pallinatura: r.riferimento_pallinatura || '',
      limite_min_assoluto: r.limite_min_assoluto || '',
      limite_max_assoluto: r.limite_max_assoluto || '',
      numero_decimali: r.numero_decimali ?? '',
      valore_conforme_okko: r.valore_conforme_okko || '',
      valore_conforme_presenza_assenza: r.valore_conforme_presenza_assenza || '',
      note_valutazione: r.note_valutazione || '',
    },
  });

  const handleRowSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...rowDialog.data };
      ['riferimento_pallinatura', 'specifica_nominale', 'tolleranza_min', 'tolleranza_max',
        'limite_min_assoluto', 'limite_max_assoluto', 'unita_misura', 'strumento_consigliato',
        'tipo_allegato_richiesto', 'note_operative', 'note_valutazione',
        'valore_conforme_okko', 'valore_conforme_presenza_assenza'].forEach(k => {
        if (data[k] === '') data[k] = null;
      });
      data.numero_decimali = data.numero_decimali === '' || data.numero_decimali === null ? null : Number(data.numero_decimali);
      // Type-dependent validation
      if (data.tipo_valutazione === 'OK / KO' && !data.valore_conforme_okko) {
        toast.error('Per tipo OK/KO devi indicare quale valore è conforme.');
        return;
      }
      if (data.tipo_valutazione === 'Presenza / Assenza' && !data.valore_conforme_presenza_assenza) {
        toast.error('Per tipo Presenza/Assenza devi indicare quale valore è conforme.');
        return;
      }
      // Remove legacy single-frequency field — always null on new schema (use applicabilita instead)
      delete data.frequenza_controllo;
      if (rowDialog.mode === 'create') {
        await qmsPlanService.createRow(id, data);
        toast.success('Riga creata');
      } else {
        await qmsPlanService.updateRow(id, data.id, data);
        toast.success('Riga aggiornata');
      }
      setRowDialog({ ...rowDialog, open: false });
      loadRows();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleObsolete = async (rowId) => {
    if (!window.confirm('Marcare questa riga come Obsoleta?')) return;
    try {
      await qmsPlanService.obsoleteRow(id, rowId);
      toast.success('Riga obsoletata');
      loadRows();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleStatoPiano = async (newStato) => {
    try {
      await qmsPlanService.update(id, { stato_piano: newStato });
      toast.success(`Piano ${newStato}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approvare il piano? Se è già stato approvato la versione verrà incrementata.')) return;
    try {
      await qmsPlanService.approve(id);
      toast.success('Piano approvato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore approvazione');
    }
  };

  const openApplicability = (row) => {
    // Build full 8-causale grid: keep existing, fill missing
    const existing = row.applicabilita || [];
    const map = {};
    existing.forEach(a => { map[a.causale_controllo] = a; });
    const apps = (enums?.causale_controllo || []).map((c, idx) => ({
      ...EMPTY_APP(c, idx),
      ...(map[c] || {}),
      causale_controllo: c,
      ordine_visualizzazione: idx,
    }));
    setAppDialog({ open: true, rowId: row.id, rowLabel: `${row.fase_processo} · N°${row.numero_caratteristica} · ${row.descrizione_caratteristica}`, apps });
  };

  const updateApp = (idx, field, value) => {
    setAppDialog(prev => ({
      ...prev,
      apps: prev.apps.map((a, i) => i === idx ? { ...a, [field]: value } : a),
    }));
  };

  const handleAppSubmit = async () => {
    try {
      // Send only active configurations (omit fully-empty rows) plus active=false rows when set explicitly
      const cleaned = appDialog.apps.map(a => ({
        causale_controllo: a.causale_controllo,
        attiva: !!a.attiva,
        obbligatoria: !!a.obbligatoria,
        frequenza_controllo: a.frequenza_controllo || null,
        quantita_campione: a.quantita_campione === '' || a.quantita_campione == null ? null : Number(a.quantita_campione),
        richiede_allegato: !!a.richiede_allegato,
        tipo_allegato_richiesto: a.tipo_allegato_richiesto || null,
        note_specifiche: a.note_specifiche || null,
        ordine_visualizzazione: a.ordine_visualizzazione ?? 0,
      }));
      await qmsPlanService.updateApplicability(id, appDialog.rowId, cleaned);
      toast.success('Applicabilità aggiornata');
      setAppDialog({ ...appDialog, open: false });
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio applicabilità');
    }
  };

  const downloadBlob = (data, filename) => {
    const blob = new Blob([data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleTemplateCsv = async () => {
    const r = await qmsPlanService.templateCsv();
    downloadBlob(r.data, 'template_piano_controlli.csv');
  };

  const handleExportCsv = async () => {
    const r = await qmsPlanService.exportCsv(id);
    downloadBlob(r.data, `piano_${plan.customer_code}_${plan.product_code}_rev${plan.revision}.csv`);
  };

  const handleImportCsv = async (file) => {
    if (!file) return;
    try {
      const r = await qmsPlanService.importCsv(id, file);
      toast.success(`Importate ${r.data.created} righe. Errori: ${r.data.errors.length}`);
      if (r.data.errors.length) console.warn('CSV import errors:', r.data.errors);
      loadRows();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore import');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDuplicate = async (e) => {
    e.preventDefault();
    try {
      const r = await qmsPlanService.duplicate(id, {
        new_revision: dupDialog.new_revision,
        new_id_configurazione: dupDialog.new_id_configurazione || null,
        new_titolo: dupDialog.new_titolo || null,
      });
      toast.success(`Piano duplicato a rev. ${dupDialog.new_revision}`);
      setDupDialog({ ...dupDialog, open: false });
      navigate(`/qms-plans/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore duplicazione');
    }
  };

  if (!plan || !enums) {
    return <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;
  }

  const tv = rowDialog.data.tipo_valutazione;
  const showNominaleTolleranze = tv === 'Numerica con nominale e tolleranze';
  const showLimiteMax = tv === 'Limite massimo' || tv === 'Range min/max assoluto';
  const showLimiteMin = tv === 'Limite minimo' || tv === 'Range min/max assoluto';
  const showOkKo = tv === 'OK / KO';
  const showPresenzaAssenza = tv === 'Presenza / Assenza';

  return (
    <div className="space-y-6 pb-12" data-testid="qms-plan-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/qms-plans"><Button variant="outline" size="sm"><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button></Link>
        <Badge className={`${STATO_BADGE[plan.stato_piano]} border`} data-testid="plan-stato">{plan.stato_piano}</Badge>
        <Badge className="bg-slate-100 text-slate-700 border border-slate-300" data-testid="plan-versione">v{plan.versione_piano_controllo || 1}</Badge>
        {plan.data_approvazione && (
          <Badge className="bg-blue-50 text-blue-700 border border-blue-200">Approvato {new Date(plan.data_approvazione).toLocaleDateString('it-IT')} da {plan.approvato_da}</Badge>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2"><FileText className="w-6 h-6" />{plan.titolo || 'Piano Controlli'}</CardTitle>
              <p className="text-sm text-slate-500 mt-1">{plan.customer_code} · {plan.customer_name} → {plan.product_code} · {plan.product_description}</p>
            </div>
            {canEdit && (
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={handleApprove} data-testid="approve-plan-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" />Approva piano
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDupDialog({ open: true, new_revision: '', new_id_configurazione: '', new_titolo: '' })} data-testid="duplicate-btn">
                  <Copy className="w-3 h-3 mr-1" />Duplica per nuova revisione
                </Button>
                <Button size="sm" variant="outline" onClick={handleTemplateCsv} data-testid="template-csv-btn">
                  <FileDown className="w-3 h-3 mr-1" />Template CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-csv-btn">
                  <Upload className="w-3 h-3 mr-1" />Importa CSV
                </Button>
                <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={e => handleImportCsv(e.target.files?.[0])} />
                <Button size="sm" variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
                  <Download className="w-3 h-3 mr-1" />Esporta CSV
                </Button>
                {plan.stato_piano === 'Attivo' ? (
                  <Button size="sm" variant="outline" onClick={() => handleStatoPiano('Obsoleto')} data-testid="obsolete-plan-btn">
                    <Archive className="w-3 h-3 mr-1" />Obsoleta piano
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleStatoPiano('Attivo')} data-testid="reactivate-plan-btn">
                    <Power className="w-3 h-3 mr-1" />Riattiva
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div><span className="text-slate-500">Revisione</span><p className="font-mono font-bold">{plan.revision}</p></div>
          <div><span className="text-slate-500">ID Configurazione</span><p className="font-mono">{plan.id_configurazione || '—'}</p></div>
          <div><span className="text-slate-500">Versione</span><p className="font-mono font-bold">v{plan.versione_piano_controllo || 1}</p></div>
          <div><span className="text-slate-500">Righe Attive</span><p className="font-mono font-bold">{plan.active_rows_count} / {plan.total_rows_count}</p></div>
          <div><span className="text-slate-500">Ultimo update</span><p className="text-xs">{plan.updated_at ? new Date(plan.updated_at).toLocaleString('it-IT') : '—'}</p></div>
          {plan.note && <div className="col-span-full"><span className="text-slate-500">Note</span><p>{plan.note}</p></div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base">Quote / Caratteristiche ({rows.length})</CardTitle>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={showObsolete} onCheckedChange={setShowObsolete} data-testid="show-obsolete" />
                Mostra obsolete
              </label>
              {canEdit && (
                <Button size="sm" onClick={openCreateRow} data-testid="new-row-btn">
                  <Plus className="w-3 h-3 mr-1" />Nuova quota
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessuna quota</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-2 pr-2">N°</th>
                    <th className="py-2 pr-2">Pall.</th>
                    <th className="py-2 pr-2">Fase</th>
                    <th className="py-2 pr-2">Caratteristica</th>
                    <th className="py-2 pr-2">Tipo / Valutazione</th>
                    <th className="py-2 pr-2">Spec.</th>
                    <th className="py-2 pr-2">Tol/Limiti</th>
                    <th className="py-2 pr-2">UM</th>
                    <th className="py-2 pr-2">Metodo</th>
                    <th className="py-2 pr-2">Applicabilità</th>
                    <th className="py-2 pr-2">Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const activeApps = (r.applicabilita || []).filter(a => a.attiva);
                    return (
                      <tr key={r.id} className={`border-b ${r.stato_riga === 'Obsoleta' ? 'opacity-50' : 'hover:bg-slate-50'}`} data-testid={`row-${r.id}`}>
                        <td className="py-2 pr-2 font-mono font-bold">{r.numero_caratteristica}</td>
                        <td className="py-2 pr-2 font-mono">{r.riferimento_pallinatura || '—'}</td>
                        <td className="py-2 pr-2 font-mono">{r.fase_processo}</td>
                        <td className="py-2 pr-2 max-w-xs">
                          <p className="truncate">{r.descrizione_caratteristica}</p>
                          {r.note_operative && <p className="text-slate-400 italic truncate">{r.note_operative}</p>}
                        </td>
                        <td className="py-2 pr-2">
                          <p className="text-[11px]">{r.tipo_caratteristica}</p>
                          <p className="text-[10px] text-slate-500">{r.tipo_valutazione}</p>
                        </td>
                        <td className="py-2 pr-2 font-mono">{r.specifica_nominale || '—'}</td>
                        <td className="py-2 pr-2 font-mono whitespace-nowrap text-[11px]">
                          {(r.tolleranza_min || r.tolleranza_max) && <p>{r.tolleranza_min || '—'} / {r.tolleranza_max || '—'}</p>}
                          {(r.limite_min_assoluto || r.limite_max_assoluto) && <p className="text-slate-500">[{r.limite_min_assoluto || '—'} ; {r.limite_max_assoluto || '—'}]</p>}
                        </td>
                        <td className="py-2 pr-2">{r.unita_misura || '—'}</td>
                        <td className="py-2 pr-2">{r.metodo_controllo}</td>
                        <td className="py-2 pr-2 max-w-[180px]">
                          {activeApps.length === 0 ? <span className="text-slate-400 italic text-[10px]">nessuna</span> :
                            <div className="flex flex-wrap gap-0.5">
                              {activeApps.map(a => (
                                <Badge key={a.causale_controllo} className="bg-blue-50 text-blue-700 border border-blue-200 text-[9px] px-1 py-0">
                                  {a.causale_controllo.slice(0, 14)}{a.obbligatoria && '*'}
                                </Badge>
                              ))}
                            </div>
                          }
                        </td>
                        <td className="py-2 pr-2"><Badge className={`${STATO_BADGE[r.stato_riga]} border`}>{r.stato_riga}</Badge></td>
                        <td className="py-2 pr-2 space-x-1 whitespace-nowrap">
                          {canEdit && r.stato_riga === 'Attiva' && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openApplicability(r)} data-testid={`apps-row-${r.id}`} title="Applicabilità">
                                <ListChecks className="w-3 h-3 text-blue-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEditRow(r)} data-testid={`edit-row-${r.id}`} title="Modifica">
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleObsolete(r.id)} data-testid={`obsolete-row-${r.id}`} title="Obsoleta">
                                <Archive className="w-3 h-3 text-amber-600" />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row Dialog */}
      <Dialog open={rowDialog.open} onOpenChange={open => setRowDialog({ ...rowDialog, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{rowDialog.mode === 'create' ? 'Nuova Quota' : 'Modifica Quota'}</DialogTitle>
            <DialogDescription>Dopo aver creato la quota, configura l&apos;applicabilità per causale (Avvio, In process, Benestare, ...).</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRowSubmit} className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label>Fase processo *</Label>
                <Select value={rowDialog.data.fase_processo} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, fase_processo: v } })}>
                  <SelectTrigger data-testid="row-fase"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.fase_processo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>N° Caratteristica *</Label>
                <Input value={rowDialog.data.numero_caratteristica} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, numero_caratteristica: e.target.value } })} required data-testid="row-numero" />
              </div>
              <div>
                <Label>Pallinatura</Label>
                <Input value={rowDialog.data.riferimento_pallinatura || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, riferimento_pallinatura: e.target.value } })} placeholder="es. A1" data-testid="row-pallinatura" />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={rowDialog.data.tipo_caratteristica} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, tipo_caratteristica: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.tipo_caratteristica.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-4">
                <Label>Descrizione caratteristica *</Label>
                <Input value={rowDialog.data.descrizione_caratteristica} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, descrizione_caratteristica: e.target.value } })} required data-testid="row-desc" />
              </div>

              <div className="col-span-2">
                <Label>Tipo valutazione *</Label>
                <Select value={rowDialog.data.tipo_valutazione} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, tipo_valutazione: v } })}>
                  <SelectTrigger data-testid="row-tipo-val"><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.tipo_valutazione.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>UM</Label>
                <Input value={rowDialog.data.unita_misura || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, unita_misura: e.target.value } })} placeholder="mm, µm..." />
              </div>
              <div>
                <Label>Decimali</Label>
                <Input type="number" min="0" max="6" value={rowDialog.data.numero_decimali ?? ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, numero_decimali: e.target.value } })} placeholder="es. 3" />
              </div>

              {showNominaleTolleranze && <>
                <div>
                  <Label>Nominale *</Label>
                  <Input value={rowDialog.data.specifica_nominale || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, specifica_nominale: e.target.value } })} className="font-mono" placeholder="es. 37,45" data-testid="row-nominale" />
                </div>
                <div>
                  <Label>Tol. min *</Label>
                  <Input value={rowDialog.data.tolleranza_min || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, tolleranza_min: e.target.value } })} className="font-mono" placeholder="es. -0,005" data-testid="row-tol-min" />
                </div>
                <div>
                  <Label>Tol. max *</Label>
                  <Input value={rowDialog.data.tolleranza_max || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, tolleranza_max: e.target.value } })} className="font-mono" placeholder="es. +0,020" data-testid="row-tol-max" />
                </div>
                <div className="col-span-1"></div>
              </>}

              {(showLimiteMax || showLimiteMin) && <>
                {showLimiteMin && <div>
                  <Label>Limite min assoluto *</Label>
                  <Input value={rowDialog.data.limite_min_assoluto || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, limite_min_assoluto: e.target.value } })} className="font-mono" data-testid="row-lim-min" />
                </div>}
                {showLimiteMax && <div>
                  <Label>Limite max assoluto *</Label>
                  <Input value={rowDialog.data.limite_max_assoluto || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, limite_max_assoluto: e.target.value } })} className="font-mono" data-testid="row-lim-max" />
                </div>}
              </>}

              {showOkKo && <div className="col-span-2">
                <Label>Valore Conforme (OK/KO) *</Label>
                <Select value={rowDialog.data.valore_conforme_okko || ''} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, valore_conforme_okko: v } })}>
                  <SelectTrigger data-testid="row-conforme-okko"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{enums.valore_okko.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">Quale valore corrisponde a Conforme?</p>
              </div>}

              {showPresenzaAssenza && <div className="col-span-2">
                <Label>Valore Conforme (Presenza/Assenza) *</Label>
                <Select value={rowDialog.data.valore_conforme_presenza_assenza || ''} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, valore_conforme_presenza_assenza: v } })}>
                  <SelectTrigger data-testid="row-conforme-pa"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{enums.valore_presenza_assenza.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">Esempio: per &quot;bava residua&quot;, Conforme = Assente.</p>
              </div>}

              <div className="col-span-2">
                <Label>Metodo controllo *</Label>
                <Select value={rowDialog.data.metodo_controllo} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, metodo_controllo: v } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{enums.metodo_controllo.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Strumento consigliato</Label>
                <Input value={rowDialog.data.strumento_consigliato || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, strumento_consigliato: e.target.value } })} placeholder="es. CAL001" />
              </div>

              <div className="col-span-4 flex items-center gap-6 pt-1 border-t">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={rowDialog.data.obbligatorio} onCheckedChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, obbligatorio: !!v } })} />
                  Obbligatoria
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={rowDialog.data.richiede_allegato} onCheckedChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, richiede_allegato: !!v } })} />
                  Richiede allegato (default)
                </label>
                {rowDialog.data.richiede_allegato && (
                  <div className="flex-1">
                    <Select value={rowDialog.data.tipo_allegato_richiesto || ''} onValueChange={v => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, tipo_allegato_richiesto: v } })}>
                      <SelectTrigger><SelectValue placeholder="Tipo allegato" /></SelectTrigger>
                      <SelectContent>{enums.tipo_allegato_richiesto.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="col-span-4">
                <Label>Note operative</Label>
                <Textarea rows={2} value={rowDialog.data.note_operative || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, note_operative: e.target.value } })} />
              </div>
              <div className="col-span-4">
                <Label>Note valutazione</Label>
                <Textarea rows={2} value={rowDialog.data.note_valutazione || ''} onChange={e => setRowDialog({ ...rowDialog, data: { ...rowDialog.data, note_valutazione: e.target.value } })} placeholder="Note specifiche sul criterio di accettazione" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRowDialog({ ...rowDialog, open: false })}>Annulla</Button>
              <Button type="submit" data-testid="row-submit">{rowDialog.mode === 'create' ? 'Crea' : 'Salva'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Applicabilità Dialog */}
      <Dialog open={appDialog.open} onOpenChange={open => setAppDialog({ ...appDialog, open })}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Applicabilità per causale</DialogTitle>
            <DialogDescription className="font-mono text-xs">{appDialog.rowLabel}</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wider text-slate-500 border-b">
                  <th className="py-2 pr-2 w-32">Causale</th>
                  <th className="py-2 px-1">Attiva</th>
                  <th className="py-2 px-1">Obbligatoria</th>
                  <th className="py-2 px-2">Frequenza</th>
                  <th className="py-2 px-2 w-20">Qtà camp.</th>
                  <th className="py-2 px-1">All.</th>
                  <th className="py-2 px-2">Tipo all.</th>
                  <th className="py-2 px-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {appDialog.apps.map((a, idx) => (
                  <tr key={a.causale_controllo} className={`border-b ${a.attiva ? 'bg-blue-50/40' : 'opacity-60'}`} data-testid={`app-row-${a.causale_controllo}`}>
                    <td className="py-2 pr-2 font-semibold">{a.causale_controllo}</td>
                    <td className="py-2 px-1 text-center">
                      <Checkbox checked={!!a.attiva} onCheckedChange={v => updateApp(idx, 'attiva', !!v)} data-testid={`app-attiva-${a.causale_controllo}`} />
                    </td>
                    <td className="py-2 px-1 text-center">
                      <Checkbox checked={!!a.obbligatoria} onCheckedChange={v => updateApp(idx, 'obbligatoria', !!v)} disabled={!a.attiva} />
                    </td>
                    <td className="py-2 px-2">
                      <Input value={a.frequenza_controllo || ''} onChange={e => updateApp(idx, 'frequenza_controllo', e.target.value)} placeholder="es. ogni 20 pz" disabled={!a.attiva} className="h-8 text-xs" />
                    </td>
                    <td className="py-2 px-2">
                      <Input type="number" min="0" value={a.quantita_campione ?? ''} onChange={e => updateApp(idx, 'quantita_campione', e.target.value)} disabled={!a.attiva} className="h-8 text-xs" />
                    </td>
                    <td className="py-2 px-1 text-center">
                      <Checkbox checked={!!a.richiede_allegato} onCheckedChange={v => updateApp(idx, 'richiede_allegato', !!v)} disabled={!a.attiva} />
                    </td>
                    <td className="py-2 px-2">
                      <Select value={a.tipo_allegato_richiesto || '__none'} onValueChange={v => updateApp(idx, 'tipo_allegato_richiesto', v === '__none' ? '' : v)} disabled={!a.attiva || !a.richiede_allegato}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">—</SelectItem>
                          {enums.tipo_allegato_richiesto.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-2">
                      <Input value={a.note_specifiche || ''} onChange={e => updateApp(idx, 'note_specifiche', e.target.value)} disabled={!a.attiva} className="h-8 text-xs" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAppDialog({ ...appDialog, open: false })}>Annulla</Button>
            <Button onClick={handleAppSubmit} data-testid="app-submit">Salva Applicabilità</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={dupDialog.open} onOpenChange={open => setDupDialog({ ...dupDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplica Piano per Nuova Revisione</DialogTitle>
            <DialogDescription>Copia tutte le righe Attive su una nuova revisione del prodotto {plan.product_code}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDuplicate} className="space-y-3">
            <div>
              <Label>Nuova revisione *</Label>
              <Input value={dupDialog.new_revision} onChange={e => setDupDialog({ ...dupDialog, new_revision: e.target.value })} required placeholder="es. 02" data-testid="dup-revision" />
            </div>
            <div>
              <Label>Nuovo ID Configurazione</Label>
              <Input value={dupDialog.new_id_configurazione} onChange={e => setDupDialog({ ...dupDialog, new_id_configurazione: e.target.value })} placeholder="opz." />
            </div>
            <div>
              <Label>Nuovo titolo</Label>
              <Input value={dupDialog.new_titolo} onChange={e => setDupDialog({ ...dupDialog, new_titolo: e.target.value })} placeholder="opz." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDupDialog({ ...dupDialog, open: false })}>Annulla</Button>
              <Button type="submit" data-testid="dup-submit">Duplica</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
